import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { CustomerGuestsApiService } from 'src/shared/services/customer-guests-api.service';
import validationUtils from 'src/shared/util/validation-utils';
import { GuestPlatformBadge, guestPlatformBadge } from 'src/shared/constants/guest-platform-badges';

import { TelephonePipe } from 'src/shared/pipes/telephone.pipe';
import { parsePhoneNumber } from 'src/tidy-ui-components/components/form/input-international-phone/international-number.utils';

import {
  CustomerGuest,
  CustomerGuestCreatePayload,
} from 'src/models/customer-guest.model';

import { SharedModule } from 'src/shared/shared.module';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';

/**
 * Emitted value of the guest picker. `existing` links an already-saved guest;
 * `new` carries a payload the caller should create via `POST customer/guests`
 * before using the resulting id. Null (no emission of a value) means "incomplete".
 */
export type GuestPickerValue =
  | { mode: 'existing'; guest: CustomerGuest }
  | { mode: 'new'; payload: CustomerGuestCreatePayload };

// Precomputed per-row view model so the template never calls functions (OnPush-friendly).
interface GuestResultRow {
  guest: CustomerGuest;
  name: string;
  contact: string;
  badge: GuestPlatformBadge;
}

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;
const MAX_RESULTS = 5;

const NON_BLANK = (control: AbstractControl): ValidationErrors | null => {
  const value = (control.value ?? '').toString().trim();
  return value.length > 0 ? null : { required: true };
};

@Component({
  selector: 'tidy-guest-picker',
  templateUrl: './guest-picker.component.html',
  styleUrls: ['./guest-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IonicModule,
    TidyUiComponentsModule,
    SharedModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class GuestPickerComponent {
  private readonly fb = inject(FormBuilder);
  private readonly guestsApi = inject(CustomerGuestsApiService);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() submitted = false;
  @Input() label = 'Search guests by name, email, or phone';

  @Output() valueChange = new EventEmitter<GuestPickerValue | null>();

  mode: 'search' | 'create' = 'search';
  selectedRow: GuestResultRow | null = null;
  resultRows: GuestResultRow[] = [];
  isSearching = false;
  hasSearched = false;
  searchQuery = '';
  hasValidValue = false;

  readonly skeletonRows = [0, 1, 2];
  readonly searchForm: FormGroup;
  readonly newGuestForm: FormGroup;

  private phoneCountryCode = '1';
  private searchRequestId = 0;

  constructor() {
    this.searchForm = this.fb.nonNullable.group({ search: '' });
    this.newGuestForm = this.fb.nonNullable.group({
      name: this.fb.nonNullable.control('', [NON_BLANK, Validators.maxLength(100)]),
      email: this.fb.nonNullable.control('', [validationUtils.validateEmail]),
      phone: this.fb.nonNullable.control(''),
    });
    this.searchForm.get('search')!.valueChanges
      .pipe(debounceTime(SEARCH_DEBOUNCE_MS), takeUntilDestroyed())
      .subscribe((value: string) => this.onSearchChanged(value ?? ''));
    this.newGuestForm.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.emitNewGuest());
  }

  selectGuest(row: GuestResultRow): void {
    this.selectedRow = row;
    this.resultRows = [];
    this.hasSearched = false;
    this.searchForm.get('search')!.setValue('', { emitEvent: false });
    this.emit({ mode: 'existing', guest: row.guest });
  }

  clearSelection(): void {
    this.selectedRow = null;
    this.searchQuery = '';
    this.emit(null);
  }

  switchToCreate(): void {
    if (this.mode === 'create') {
      return;
    }
    this.mode = 'create';
    this.selectedRow = null;
    this.emitNewGuest();
  }

  switchToSearch(): void {
    if (this.mode === 'search') {
      return;
    }
    this.mode = 'search';
    this.emit(this.selectedRow ? { mode: 'existing', guest: this.selectedRow.guest } : null);
  }

  // Empty-state shortcut: carry the typed query into the new-guest form.
  createFromSearch(): void {
    const name = this.searchQuery;
    this.switchToCreate();
    if (name && !this.newGuestForm.get('name')!.value) {
      this.newGuestForm.patchValue({ name });
    }
  }

  onCountryCodeChange(countryCode: string): void {
    this.phoneCountryCode = countryCode.replace('+', '');
    this.emitNewGuest();
  }

  private async onSearchChanged(value: string): Promise<void> {
    const trimmed = value.trim();
    this.searchQuery = trimmed;
    if (trimmed.length < MIN_SEARCH_LENGTH) {
      this.resultRows = [];
      this.hasSearched = false;
      this.isSearching = false;
      this.cdr.markForCheck();
      return;
    }
    const requestId = ++this.searchRequestId;
    this.isSearching = true;
    this.cdr.markForCheck();
    try {
      const page = await firstValueFrom(
        this.guestsApi.listGuests({ page: 1, perPage: MAX_RESULTS, filters: this.buildFilters(trimmed) })
      );
      if (requestId !== this.searchRequestId) {
        return;
      }
      this.resultRows = page.guests.map((guest) => this.toRow(guest));
      this.hasSearched = true;
    } catch {
      if (requestId !== this.searchRequestId) {
        return;
      }
      this.resultRows = [];
      this.hasSearched = true;
    } finally {
      if (requestId === this.searchRequestId) {
        this.isSearching = false;
        this.cdr.markForCheck();
      }
    }
  }

  private emitNewGuest(): void {
    if (this.mode !== 'create') {
      return;
    }
    if (!this.newGuestForm.valid) {
      this.emit(null);
      return;
    }
    const value = this.newGuestForm.getRawValue();
    const name = (value.name ?? '').trim();
    const email = (value.email ?? '').trim();
    const phone = this.normalizePhone(value.phone);
    const payload: CustomerGuestCreatePayload = {
      name,
      ...(email ? { email } : {}),
      ...(phone ? { phone } : {}),
    };
    this.emit({ mode: 'new', payload });
  }

  private emit(value: GuestPickerValue | null): void {
    this.hasValidValue = value !== null;
    this.valueChange.emit(value);
    this.cdr.markForCheck();
  }

  private buildFilters(query: string): { name?: string; email?: string; phone?: string } {
    if (query.includes('@')) {
      return { email: query };
    }
    if (/^[\d\s()+-]+$/.test(query)) {
      return { phone: query };
    }
    return { name: query };
  }

  private normalizePhone(value: string | null | undefined): string {
    const phone = (value ?? '').trim();
    if (!phone) {
      return '';
    }
    const phoneWithCountryCode = phone.startsWith('+') ? phone.slice(1) : `${this.phoneCountryCode}${phone}`;
    return parsePhoneNumber(phoneWithCountryCode);
  }

  private toRow(guest: CustomerGuest): GuestResultRow {
    const name = (guest.name ?? '').trim() || 'Unnamed guest';
    const phone = guest.phone ? new TelephonePipe().transform(guest.phone, 'format') : '';
    const contact = (guest.email ?? '').trim() || phone;
    return {
      guest,
      name,
      contact,
      badge: guestPlatformBadge(guest.platform),
    };
  }
}
